<template>
  <Teleport :to="teleportTo">
    <Transition name="backdrop">
      <div
        v-if="modelValue"
        class="ui-drawer-backdrop"
        :class="{ 'ui-drawer-backdrop-clickable': closeOnBackdrop }"
        @click="handleBackdropClick"
      />
    </Transition>
    <Transition :name="animationTransitionName">
      <aside
        v-if="modelValue"
        ref="drawerRef"
        class="ui-drawer"
        :class="drawerClasses"
        :style="drawerStyles"
        role="dialog"
        aria-modal="true"
        @keydown.esc="handleEscKey"
      >
        <header v-if="$slots.header" class="ui-drawer-header">
          <slot name="header" />
        </header>

        <div class="ui-drawer-content">
          <slot />
        </div>

        <footer v-if="$slots.footer" class="ui-drawer-footer">
          <slot name="footer" />
        </footer>
      </aside>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, watch, onMounted, onBeforeUnmount, ref, nextTick } from 'vue'

interface Props {
  modelValue: boolean
  side: 'left' | 'right' | 'top' | 'bottom'
  borderRadius?: string
  width?: string
  height?: string
  backdrop?: boolean
  closeOnBackdrop?: boolean
  closeOnEsc?: boolean
  trapFocus?: boolean
  teleportTo?: string
  animation?: 'slide' | 'slide-fade'
}

const props = withDefaults(defineProps<Props>(), {
  width: '80vw',
  height: '70vh',
  backdrop: true,
  closeOnBackdrop: true,
  closeOnEsc: true,
  trapFocus: true,
  teleportTo: 'body',
  animation: 'slide',
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  open: []
  close: []
}>()

const drawerRef = ref<HTMLElement | null>(null)
let focusableElements: HTMLElement[] = []
let firstFocusableElement: HTMLElement | null = null
let lastFocusableElement: HTMLElement | null = null

// Computed properties
const isHorizontal = computed(() => props.side === 'left' || props.side === 'right')

const drawerClasses = computed(() => {
  return {
    [`ui-drawer-${props.side}`]: true,
  }
})

const drawerStyles = computed(() => {
  const styles: Record<string, string> = {}

  if (isHorizontal.value) {
    styles.width = props.width
  } else {
    styles.height = props.height
    styles.maxHeight = props.height
  }

  if (props.borderRadius) {
    switch (props.side) {
      case 'left':
        styles.borderTopRightRadius = props.borderRadius
        styles.borderBottomRightRadius = props.borderRadius
        break
      case 'right':
        styles.borderTopLeftRadius = props.borderRadius
        styles.borderBottomLeftRadius = props.borderRadius
        break
      case 'top':
        styles.borderBottomLeftRadius = props.borderRadius
        styles.borderBottomRightRadius = props.borderRadius
        break
      case 'bottom':
        styles.borderTopLeftRadius = props.borderRadius
        styles.borderTopRightRadius = props.borderRadius
        break
    }
  }

  return styles
})

const animationTransitionName = computed(() => {
  return props.animation === 'slide-fade' ? 'slide-fade' : 'slide'
})

const close = (): void => {
  emit('update:modelValue', false)
  emit('close')
}

const open = (): void => {
  emit('open')
}

const handleBackdropClick = (event: MouseEvent): void => {
  if (props.closeOnBackdrop && event.target === event.currentTarget) {
    close()
  }
}

const handleEscKey = (): void => {
  if (props.closeOnEsc) {
    close()
  }
}

const getFocusableElements = (): HTMLElement[] => {
  if (!drawerRef.value) return []

  const selector = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ')

  return Array.from(drawerRef.value.querySelectorAll<HTMLElement>(selector))
}

const trapFocus = (event: KeyboardEvent): void => {
  if (!props.trapFocus || !drawerRef.value) return

  if (event.key !== 'Tab') return

  if (focusableElements.length === 0) {
    focusableElements = getFocusableElements()
    firstFocusableElement = focusableElements[0] || null
    lastFocusableElement = focusableElements[focusableElements.length - 1] || null
  }

  if (focusableElements.length === 0) {
    event.preventDefault()
    return
  }

  if (event.shiftKey) {
    if (document.activeElement === firstFocusableElement) {
      event.preventDefault()
      lastFocusableElement?.focus()
    }
  } else {
    if (document.activeElement === lastFocusableElement) {
      event.preventDefault()
      firstFocusableElement?.focus()
    }
  }
}

watch(
  () => props.modelValue,
  async newValue => {
    if (newValue) {
      if (typeof document !== 'undefined') {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
        document.body.style.overflow = 'hidden'
        if (scrollbarWidth > 0) {
          document.body.style.paddingRight = `${scrollbarWidth}px`
        }
      }
      open()
      await nextTick()
      if (props.trapFocus && drawerRef.value) {
        focusableElements = getFocusableElements()
        firstFocusableElement = focusableElements[0] || null
        lastFocusableElement = focusableElements[focusableElements.length - 1] || null

        if (firstFocusableElement) {
          firstFocusableElement.focus()
        } else if (drawerRef.value) {
          drawerRef.value.focus()
        }

        drawerRef.value.addEventListener('keydown', trapFocus)
      }
    } else {
      if (typeof document !== 'undefined') {
        document.body.style.overflow = ''
        document.body.style.paddingRight = ''
      }
      if (drawerRef.value) {
        drawerRef.value.removeEventListener('keydown', trapFocus)
      }
      focusableElements = []
      firstFocusableElement = null
      lastFocusableElement = null
    }
  }
)

onMounted(() => {
  if (props.modelValue) {
    if (typeof document !== 'undefined') {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
      document.body.style.overflow = 'hidden'
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`
      }
    }
    open()
  }
})

onBeforeUnmount(() => {
  if (typeof document !== 'undefined') {
    document.body.style.overflow = ''
    document.body.style.paddingRight = ''
  }
  if (drawerRef.value) {
    drawerRef.value.removeEventListener('keydown', trapFocus)
  }
})
</script>

<style scoped>
.ui-drawer-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9998;
  transition: opacity 0.3s ease;
}

.ui-drawer-backdrop-clickable {
  cursor: pointer;
}

.ui-drawer {
  position: fixed;
  background-color: white;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  outline: none;
}

.ui-drawer-left {
  top: 0;
  left: 0;
  height: 100vh;
  height: 100dvh;
  max-width: 100vw;
}

.ui-drawer-right {
  top: 0;
  right: 0;
  height: 100vh;
  height: 100dvh;
  max-width: 100vw;
}

.ui-drawer-top {
  top: 0;
  left: 0;
  right: 0;
  width: 100vw;
  max-height: 100vh;
  max-height: 100dvh;
}

.ui-drawer-bottom {
  bottom: 0;
  left: 0;
  right: 0;
  width: 100vw;
}

.ui-drawer-header {
  padding: 0.5rem;
  flex-shrink: 0;
}

.ui-drawer-content {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: 1rem;
}

.ui-drawer-footer {
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(0);
}

.ui-drawer-left.slide-enter-from,
.ui-drawer-left.slide-leave-to {
  transform: translateX(-100%);
}

.ui-drawer-right.slide-enter-from,
.ui-drawer-right.slide-leave-to {
  transform: translateX(100%);
}

.ui-drawer-top.slide-enter-from,
.ui-drawer-top.slide-leave-to {
  transform: translateY(-100%);
}

.ui-drawer-bottom.slide-enter-from,
.ui-drawer-bottom.slide-leave-to {
  transform: translateY(100%);
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition:
    transform 0.3s ease,
    opacity 0.3s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
}

.ui-drawer-left.slide-fade-enter-from,
.ui-drawer-left.slide-fade-leave-to {
  transform: translateX(-100%);
}

.ui-drawer-right.slide-fade-enter-from,
.ui-drawer-right.slide-fade-leave-to {
  transform: translateX(100%);
}

.ui-drawer-top.slide-fade-enter-from,
.ui-drawer-top.slide-fade-leave-to {
  transform: translateY(-100%);
}

.ui-drawer-bottom.slide-fade-enter-from,
.ui-drawer-bottom.slide-fade-leave-to {
  transform: translateY(100%);
}

.backdrop-enter-active,
.backdrop-leave-active {
  transition: opacity 0.3s ease;
}

.backdrop-enter-from,
.backdrop-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .ui-drawer-left,
  .ui-drawer-right {
    width: 100vw !important;
    max-width: 100vw;
  }

  .ui-drawer-top,
  .ui-drawer-bottom {
    height: 100vh !important;
    height: 100dvh !important;
    max-height: 100vh;
    max-height: 100dvh;
  }
}
</style>
